import React from "react";

type HelmetProps = {
  children?: React.ReactNode;
};

const ATTRIBUTE_NAME_MAP: Record<string, string> = {
  charSet: "charset",
  httpEquiv: "http-equiv",
  crossOrigin: "crossorigin",
};

const collectChildren = (node: React.ReactNode, acc: React.ReactElement[]): void => {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      const fragmentProps = child.props as { children?: React.ReactNode };
      collectChildren(fragmentProps.children, acc);
      return;
    }
    acc.push(child);
  });
};

export const Helmet: React.FC<HelmetProps> = ({ children }) => {
  const insertedNodesRef = React.useRef<HTMLElement[]>([]);
  const previousTitleRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (typeof document === "undefined") return;

    insertedNodesRef.current.forEach((node) => node.remove());
    insertedNodesRef.current = [];

    if (previousTitleRef.current !== null) {
      document.title = previousTitleRef.current;
      previousTitleRef.current = null;
    }

    const head = document.head;
    const flatChildren: React.ReactElement[] = [];
    collectChildren(children, flatChildren);

    let nextTitle: string | null = null;

    flatChildren.forEach((element) => {
      if (typeof element.type !== "string") return;

      if (element.type === "title") {
        const titleProps = element.props as { children?: React.ReactNode };
        nextTitle = String(titleProps.children ?? "");
        return;
      }

      if (element.type !== "meta" && element.type !== "script" && element.type !== "link") return;

      const node = document.createElement(element.type);
      Object.entries(element.props).forEach(([key, value]) => {
        if (key === "children" || value == null) return;
        const attrName = ATTRIBUTE_NAME_MAP[key] ?? key;

        if (typeof value === "boolean") {
          if (value) node.setAttribute(attrName, "");
          return;
        }

        node.setAttribute(attrName, String(value));
      });

      if (element.type === "script") {
        const scriptProps = element.props as { children?: React.ReactNode };
        if (scriptProps.children != null) {
          (node as HTMLScriptElement).text = String(scriptProps.children);
        }
      }

      head.appendChild(node);
      insertedNodesRef.current.push(node);
    });

    if (nextTitle !== null) {
      previousTitleRef.current = document.title;
      document.title = nextTitle;
    }

    return () => {
      insertedNodesRef.current.forEach((node) => node.remove());
      insertedNodesRef.current = [];

      if (previousTitleRef.current !== null) {
        document.title = previousTitleRef.current;
        previousTitleRef.current = null;
      }
    };
  }, [children]);

  return null;
};
